<template>
  <b-modal v-model="modal" :title="title" size="xl" :no-enforce-focus="true" :no-close-on-backdrop="true">
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveUser()" autocomplete="off">
          <div class="row no-gutters">
            <!-- Coluna Esquerda: Informações do Usuário -->
            <div class="col-left">
              <!-- Informações do Usuário -->
              <div class="mb-4">
                <h6 class="section-title mb-3">
                  <i class="mdi mdi-account-circle text-primary mr-2"></i>
                  Informações do Usuário
                </h6>
                <div class="form-group">
                  <label for="name">Nome Completo</label>
                  <div class="input-with-icon">
                    <i class="mdi mdi-account"></i>
                    <input
                      id="name"
                      v-model="user.name"
                      name="name"
                      type="text"
                      class="form-control"
                      placeholder="Digite o nome completo"
                      :readonly="user.is_tablet"
                      :class="{
                        'is-invalid': submitted && $v.user.name.$error
                      }"
                    />
                  </div>
                  <div
                    v-if="submitted && !$v.user.name.required"
                    class="invalid-feedback d-block"
                  >
                    O campo nome é obrigatório.
                  </div>
                </div>

                <div class="form-group">
                  <label for="email">E-mail</label>
                  <div class="input-with-icon">
                    <i class="mdi mdi-email"></i>
                    <input
                      id="email"
                      v-model="user.email"
                      name="email"
                      type="email"
                      class="form-control"
                      placeholder="email@exemplo.com"
                      :readonly="user.is_tablet"
                      :class="{
                        'is-invalid': submitted && $v.user.email.$error
                      }"
                    />
                  </div>
                  <div
                    v-if="submitted && !$v.user.email.required"
                    class="invalid-feedback d-block"
                  >
                    O campo email é obrigatório.
                  </div>
                </div>

                <div class="form-group mb-0">
                  <label for="password">Senha</label>
                  <div class="input-with-icon">
                    <i class="mdi mdi-lock"></i>
                    <input
                      v-if="!user.id"
                      id="password"
                      v-model="user.password"
                      name="password"
                      type="password"
                      class="form-control"
                      placeholder="Digite uma senha segura"
                      :class="{
                        'is-invalid': submitted && $v.user.password.$error
                      }"
                    />
                    <input
                      v-else
                      id="password"
                      v-model="user.password"
                      name="password"
                      type="password"
                      class="form-control"
                      placeholder="Deixe em branco para não alterar"
                    />
                  </div>
                  <div
                    v-if="submitted && !user.id && !$v.user.password.required"
                    class="invalid-feedback d-block"
                  >
                    O campo senha é obrigatório.
                  </div>
                  <small v-if="user.id" class="text-muted">Deixe em branco para manter a senha atual</small>
                </div>
              </div>

              <!-- Nível de Acesso -->
              <div>
                <h6 class="section-title mb-3">
                  <i class="mdi mdi-shield-account text-primary mr-2"></i>
                  Nível de Acesso
                </h6>
                  <!-- Super Admin -->
                  <div
                    class="role-option role-super-admin"
                    :class="{ 'role-selected': user.is_super_admin }"
                    @click="selectSuperAdmin"
                  >
                    <div class="role-header">
                      <strong>
                        Super Administrador <i class="mdi mdi-shield-star text-warning"></i>
                      </strong>
                    </div>
                    <p class="role-description">
                      Acesso total ao sistema com todas as permissões ativadas. Pode gerenciar usuários, configurações e todos os módulos.
                    </p>
                  </div>

                  <!-- Master -->
                  <div
                    v-if="getMasterRoleId()"
                    class="role-option"
                    :class="{ 'role-selected': !user.is_super_admin && user.role_id === getMasterRoleId() }"
                    @click="selectRole(getMasterRoleId())"
                  >
                    <div class="role-header">
                      <strong>Master</strong>
                    </div>
                    <p class="role-description">
                      Controle avançado do sistema com permissões personalizáveis para gerenciar eventos e funcionalidades principais.
                    </p>
                  </div>

                  <!-- Admin -->
                  <div
                    v-if="getAdminRoleId()"
                    class="role-option"
                    :class="{ 'role-selected': !user.is_super_admin && user.role_id === getAdminRoleId() }"
                    @click="selectRole(getAdminRoleId())"
                  >
                    <div class="role-header">
                      <strong>Administrador</strong>
                    </div>
                    <p class="role-description">
                      Gerencia eventos e usuários com permissões personalizáveis por módulo.
                    </p>
                  </div>

                  <!-- Operator -->
                  <div
                    v-if="getOperatorRoleId()"
                    class="role-option"
                    :class="{ 'role-selected': !user.is_super_admin && user.role_id === getOperatorRoleId() }"
                    @click="selectRole(getOperatorRoleId())"
                  >
                    <div class="role-header">
                      <strong>Operador</strong>
                    </div>
                    <p class="role-description">
                      Acesso limitado para operações do dia-a-dia como check-in e gerenciamento de convidados.
                    </p>
                  </div>

                  <!-- Viewer -->
                  <div
                    v-if="getViewerRoleId()"
                    class="role-option"
                    :class="{ 'role-selected': !user.is_super_admin && !user.is_tablet && user.role_id === getViewerRoleId() }"
                    @click="selectRole(getViewerRoleId())"
                  >
                    <div class="role-header">
                      <strong>Visualizador</strong>
                    </div>
                    <p class="role-description">
                      Apenas visualização de dados, sem permissões de edição ou exclusão.
                    </p>
                  </div>

                  <!-- Tablet (credenciamento) — conta única, criada só quando ainda não existe -->
                  <div
                    v-if="!user.id && !tabletExists"
                    class="role-option"
                    :class="{ 'role-selected': user.is_tablet }"
                    @click="selectTablet"
                  >
                    <div class="role-header">
                      <strong>Tablet <i class="mdi mdi-tablet text-info"></i></strong>
                    </div>
                    <p class="role-description">
                      Conta de credenciamento usada nos tablets. Sem permissões de módulo.
                      Preenchida automaticamente e criada uma única vez.
                    </p>
                  </div>
                  <div v-else-if="!user.id && tabletExists" class="role-option role-disabled">
                    <div class="role-header">
                      <strong>Tablet <i class="mdi mdi-tablet"></i></strong>
                    </div>
                    <p class="role-description">
                      O usuário de credenciamento já existe — só pode ser criado uma vez.
                    </p>
                  </div>
              </div>

              <!-- Tags Permitidas (Check-in) — só pra não-super-admin e não-tablet -->
              <div v-if="!user.is_super_admin && !user.is_tablet" class="mt-4">
                <h6 class="section-title mb-2">
                  <i class="mdi mdi-tag-multiple text-primary mr-2"></i>
                  Tags Permitidas (Check-in)
                </h6>
                <multiselect
                  v-model="selectedAllowedTags"
                  :options="tagsList"
                  :multiple="true"
                  track-by="id"
                  label="name"
                  placeholder="Selecionar tags..."
                  :close-on-select="false"
                  tag-placeholder=""
                  selected-label=""
                  select-label=""
                  deselect-label=""
                />
                <small class="text-muted d-block mt-2" style="font-size: 0.75rem;">
                  Deixe vazio para dar acesso a todas as tags. Se preenchido, o operador só verá essas tags durante o check-in.
                </small>
              </div>

              <!-- Estações Permitidas (Check-in) — só pra não-super-admin e não-tablet -->
              <div v-if="!user.is_super_admin && !user.is_tablet" class="mt-4">
                <h6 class="section-title mb-2">
                  <i class="mdi mdi-cellphone-check text-primary mr-2"></i>
                  Estações Permitidas (Check-in)
                </h6>
                <div class="d-flex flex-wrap" style="gap: 8px;">
                  <label
                    v-for="s in stationOptions"
                    :key="s.value"
                    class="station-chip"
                    :class="{ active: stationsModel.includes(s.value) }"
                  >
                    <input type="checkbox" :value="s.value" v-model="stationsModel" class="station-chip-input"/>
                    <i :class="s.icon"></i>
                    {{ s.label }}
                  </label>
                </div>
                <small class="text-muted d-block mt-2" style="font-size: 0.75rem;">
                  Deixe vazio para liberar todas. Se preenchido, o operador só verá essas estações no /check-in.
                </small>
              </div>
            </div>

            <!-- Coluna Direita: Permissões dos Módulos — tablet não tem permissões -->
            <div v-if="!user.is_tablet" class="col-right">
              <div class="permissions-scroll-wrapper">
                <div class="permissions-header">
                  <h6 class="section-title mb-2">
                    <i class="mdi mdi-view-module text-primary mr-2"></i>
                    Permissões dos Módulos
                  </h6>
                  <p class="text-muted mb-3" style="font-size: 0.75rem;">
                    Personalize o acesso para cada módulo
                  </p>
                </div>

                <!-- Role Permissions Notice -->
                <div v-if="user.role_id && !user.use_custom_permissions && !user.is_super_admin" class="permissions-notice">
                  <i class="mdi mdi-information"></i>
                  Este usuário terá as permissões padrão do perfil selecionado.
                  <a href="#" @click.prevent="user.use_custom_permissions = true" class="ml-2">Personalizar permissões</a>
                </div>

                <!-- Module Permissions List -->
                <div v-else-if="user.role_id || user.is_super_admin" class="module-permissions-content">
                  <div
                    v-for="(groupPermissions, groupName) in groupedPermissions"
                    :key="groupName"
                    class="module-permission-item"
                  >
                    <div class="module-header" @click="toggleGroup(groupName)">
                      <div class="module-info">
                        <i :class="getGroupIcon(groupName)" class="module-icon"></i>
                        <div class="module-details">
                          <h6>{{ groupLabels[groupName] || groupName }}</h6>
                          <span class="module-description">{{ getGroupDescription(groupName) }}</span>
                        </div>
                      </div>
                      <div class="module-stats">
                        <span class="permission-count" :class="getGroupStatusClass(groupName, groupPermissions)">
                          {{ countSelectedInGroup(groupName, groupPermissions) }} de {{ groupPermissions.length }} ativas
                        </span>
                        <i
                          class="mdi transition-icon"
                          :class="isGroupOpen(groupName) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                        ></i>
                      </div>
                    </div>

                    <b-collapse
                      :id="`collapse-${groupName}`"
                      v-model="openGroups[groupName]"
                      class="module-permissions-body"
                    >
                      <div class="permission-list">
                        <div v-for="permission in groupPermissions" :key="permission.id" class="permission-item">
                          <div class="permission-info">
                            <strong>{{ permission.label }}</strong>
                            <span v-if="permission.description" class="permission-description">
                              {{ permission.description }}
                            </span>
                          </div>
                          <div class="custom-control custom-switch">
                            <input
                              type="checkbox"
                              class="custom-control-input"
                              :id="`perm_${permission.id}`"
                              :value="permission.id"
                              v-model="user.custom_permissions"
                              :disabled="user.is_super_admin"
                            >
                            <label class="custom-control-label" :for="`perm_${permission.id}`"></label>
                          </div>
                        </div>
                      </div>
                    </b-collapse>
                  </div>
                </div>

                <!-- No Role Selected -->
                <div v-else class="permissions-notice">
                  <i class="mdi mdi-alert-circle"></i>
                  Selecione um nível de acesso para configurar as permissões.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close()">Cancelar</b-button>
      <button type="submit" class="btn btn-primary mr-1" @click="saveUser()">Salvar</button>
    </template>
  </b-modal>
</template>
<script>
// Import component
import Loading from 'vue-loading-overlay'
import Multiselect from 'vue-multiselect'
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  components: {
    Loading,
    Multiselect
  },
  props: {
    iduser: {
      type: Number || String,
      default: null
    }
  },
  data () {
    return {
      title: 'Novo Usuário',
      loading: true,
      fullPage: true,
      submitted: false,
      user: {
        name: '',
        email: '',
        password: '',
        role_id: '',
        is_super_admin: false,
        is_tablet: false,
        use_custom_permissions: false,
        custom_permissions: [],
        allowed_tag_ids: [],
        allowed_stations: null
      },
      // Estações do /check-in que o operador pode acessar (checkbox multi).
      stationOptions: [
        { value: 'checkin', label: 'Check-in', icon: 'bx bx-user-check' },
        { value: 'lead', label: 'Coleta de Lead', icon: 'bx bx-clipboard' },
        { value: 'brinde', label: 'Brinde', icon: 'bx bx-gift' }
      ],
      // Já existe um usuário de tablet? (conta única) — some a opção se sim.
      tabletExists: false,
      modal: false,
      openGroups: {},
      groupLabels: {
        'menu': 'Menu e Navegação',
        'guests': 'Convidados',
        'participants': 'Inscritos',
        'housing': 'Hospedagem',
        'checkin': 'Check-in',
        'campaigns': 'Campanhas',
        'reports': 'Relatórios',
        'admin': 'Administração',
        'settings': 'Configurações',
        'payments': 'Bilheteria',
        'analytics': 'Analytics',
        'journeys': 'Jornadas',
        'registration_controls': 'Controle de Inscrição',
        'storage': 'Guarda Volumes',
        'seating': 'Assentos',
        'flight-blocks': 'Bloqueios Aéreos'
      },
      groupDescriptions: {
        'menu': 'Personalização de interface e acesso ao menu',
        'guests': 'Gerenciamento de lista de convidados',
        'participants': 'Gerenciamento de inscritos no evento',
        'housing': 'Serviços de hospedagem e alocação de quartos',
        'checkin': 'Check-in de convidados e rastreamento de presença',
        'campaigns': 'Gerenciamento de campanhas de email e SMS',
        'reports': 'Visualização e exportação de relatórios',
        'admin': 'Configurações administrativas do sistema',
        'settings': 'Configurações gerais da plataforma',
        'payments': 'Ingressos, cupons e pagamentos',
        'analytics': 'Análise de tráfego e conversões',
        'journeys': 'Automações e fluxos de comunicação',
        'registration_controls': 'Categorias e controle de vagas',
        'storage': 'Gestão de pertences dos participantes',
        'seating': 'Mapa de assentos e alocação',
        'flight-blocks': 'Templates de voos de IDA e VOLTA para aplicação em lote'
      },
      groupIcons: {
        'menu': 'mdi mdi-menu',
        'guests': 'mdi mdi-account-group',
        'participants': 'mdi mdi-account-multiple',
        'housing': 'mdi mdi-home-city',
        'checkin': 'mdi mdi-calendar-check',
        'campaigns': 'mdi mdi-bullhorn',
        'reports': 'mdi mdi-chart-bar',
        'admin': 'mdi mdi-cog',
        'settings': 'mdi mdi-tune',
        'payments': 'mdi mdi-cash-register',
        'analytics': 'mdi mdi-chart-line',
        'journeys': 'mdi mdi-map-marker-path',
        'registration_controls': 'mdi mdi-clipboard-check',
        'storage': 'mdi mdi-locker-multiple',
        'seating': 'mdi mdi-seat',
        'flight-blocks': 'mdi mdi-airplane'
      }
    }
  },
  validations () {
    if (this.user.id) {
      return {
        user: {
          name: { required },
          email: { required },
          role_id: {
            required: requiredIf(function () {
              return !this.user.is_super_admin && !this.user.is_tablet
            })
          }
        }
      }
    } else {
      return {
        user: {
          name: { required },
          email: { required },
          password: { required },
          role_id: {
            required: requiredIf(function () {
              return !this.user.is_super_admin && !this.user.is_tablet
            })
          }
        }
      }
    }
  },
  computed: {
    ...mapState('users', {
      data: 'admin_user',
      roles: 'roles',
      permissions: 'permissions',
      tagsList: 'tagsList',
      adminUsersList: 'admin_users'
    }),
    selectedAllowedTags: {
      // Mapeia ids → objetos da tagsList pra o multiselect renderizar nomes
      get () {
        if (!Array.isArray(this.user.allowed_tag_ids) || !Array.isArray(this.tagsList)) return []
        return this.tagsList.filter(t => this.user.allowed_tag_ids.includes(t.id))
      },
      set (selected) {
        this.user.allowed_tag_ids = (selected || []).map(t => t.id)
      }
    },
    // Estações permitidas (checkbox multi). Vazio => null (= todas).
    stationsModel: {
      get () {
        return Array.isArray(this.user.allowed_stations) ? this.user.allowed_stations : []
      },
      set (v) {
        this.user.allowed_stations = (v && v.length) ? v : null
      }
    },
    groupedPermissions () {
      const grouped = {}
      if (!Array.isArray(this.permissions)) {
        return grouped
      }
      this.permissions.forEach(permission => {
        const group = permission.group || 'other'
        if (!grouped[group]) {
          grouped[group] = []
        }
        grouped[group].push(permission)
      })
      return grouped
    },
    totalPermissionsCount () {
      return this.permissions ? this.permissions.length : 0
    }
  },
  methods: {
    ...mapActions('users', {
      getUser: 'getUser',
      postData: 'save',
      getRoles: 'getRoles',
      getPermissions: 'getPermissions',
      getTags: 'getTags',
      getAllUsers: 'getAll'
    }),
    selectTablet () {
      // Conta de credenciamento: sem role, sem permissões, dados fixos.
      this.user.is_tablet = true
      this.user.is_super_admin = false
      this.user.role_id = ''
      this.user.use_custom_permissions = false
      this.user.custom_permissions = []
      this.user.allowed_tag_ids = []
      this.user.name = 'Credenciamento'
      this.user.email = 'credenciamento@eventfy.com.br'
      this.user.password = '909090'
    },
    async checkTabletExists () {
      try {
        // getAll popula o state (admin_users); a action não devolve a resposta.
        await this.getAllUsers({ limit: 1000 })
        var list = Array.isArray(this.adminUsersList) ? this.adminUsersList : []
        this.tabletExists = list.some(u => Number(u.type) === 2)
      } catch (e) {
        this.tabletExists = false
      }
    },
    toggleGroup (groupName) {
      this.$set(this.openGroups, groupName, !this.openGroups[groupName])
    },
    isGroupOpen (groupName) {
      return this.openGroups[groupName] || false
    },
    countSelectedInGroup (groupName, groupPermissions) {
      return groupPermissions.filter(p => this.user.custom_permissions.includes(p.id)).length
    },
    isAllSelectedInGroup (groupName, groupPermissions) {
      return groupPermissions.every(p => this.user.custom_permissions.includes(p.id))
    },
    toggleAllInGroup (groupName, groupPermissions) {
      const allSelected = this.isAllSelectedInGroup(groupName, groupPermissions)
      if (allSelected) {
        // Desmarcar todos deste grupo
        groupPermissions.forEach(permission => {
          const index = this.user.custom_permissions.indexOf(permission.id)
          if (index > -1) {
            this.user.custom_permissions.splice(index, 1)
          }
        })
      } else {
        // Marcar todos deste grupo
        groupPermissions.forEach(permission => {
          if (!this.user.custom_permissions.includes(permission.id)) {
            this.user.custom_permissions.push(permission.id)
          }
        })
      }
    },
    getGroupBadgeClass (groupName, groupPermissions) {
      const selected = this.countSelectedInGroup(groupName, groupPermissions)
      const total = groupPermissions.length

      if (selected === 0) {
        return 'badge-soft-secondary'
      } else if (selected === total) {
        return 'badge-soft-success'
      } else {
        return 'badge-soft-warning'
      }
    },
    getGroupIcon (groupName) {
      return this.groupIcons[groupName] || 'mdi mdi-cube'
    },
    getGroupDescription (groupName) {
      return this.groupDescriptions[groupName] || ''
    },
    getGroupStatusClass (groupName, groupPermissions) {
      const selected = this.countSelectedInGroup(groupName, groupPermissions)
      const total = groupPermissions.length

      if (selected === total) {
        return 'status-full'
      } else if (selected > 0) {
        return 'status-partial'
      } else {
        return 'status-none'
      }
    },
    selectSuperAdmin () {
      this.user.is_super_admin = true
      this.user.is_tablet = false
      this.user.role_id = ''
      this.user.use_custom_permissions = false

      // Marcar todas as permissões para visualização
      if (this.permissions && Array.isArray(this.permissions) && this.permissions.length > 0) {
        this.user.custom_permissions = this.permissions.map(p => p.id)
      } else {
        this.user.custom_permissions = []
      }
    },
    selectRole (roleId) {
      if (!roleId) return
      this.user.is_super_admin = false
      this.user.is_tablet = false
      this.user.role_id = roleId

      // Se já está editando e já tem permissões customizadas, mantém
      // Senão, ativa customização e carrega permissões do role
      if (!this.user.id || !this.user.use_custom_permissions) {
        this.user.use_custom_permissions = true

        // Carregar permissões do role selecionado
        const selectedRole = this.roles.find(r => r.id === roleId)
        if (selectedRole && selectedRole.permissions && Array.isArray(selectedRole.permissions)) {
          this.user.custom_permissions = selectedRole.permissions.map(p => p.id)
        }
      }
    },
    getAdminRoleId () {
      const adminRole = this.roles.find(r => r.name === 'admin' || r.name === 'administrator')
      return adminRole ? adminRole.id : null
    },
    getOperatorRoleId () {
      const operatorRole = this.roles.find(r => r.name === 'operator')
      return operatorRole ? operatorRole.id : null
    },
    getViewerRoleId () {
      const viewerRole = this.roles.find(r => r.name === 'viewer')
      return viewerRole ? viewerRole.id : null
    },
    getMasterRoleId () {
      const masterRole = this.roles.find(r => r.name === 'master')
      return masterRole ? masterRole.id : null
    },
    async loadItem (id) {
      if (id) {
        this.title = 'Editar Usuário'
        await this.getUser(id)
        this.user = { ...this.data }

        // Converter permissões para array de IDs
        if (this.user.custom_permissions && Array.isArray(this.user.custom_permissions)) {
          this.user.custom_permissions = this.user.custom_permissions.map(p => p.id)
        } else {
          this.user.custom_permissions = []
        }

        // Hidratar tags permitidas (vem do backend já como array de IDs)
        this.user.allowed_tag_ids = Array.isArray(this.data.allowed_tag_ids)
          ? [...this.data.allowed_tag_ids]
          : []

        // Hidratar estações permitidas (null/[] = todas)
        this.user.allowed_stations = Array.isArray(this.data.allowed_stations)
          ? [...this.data.allowed_stations]
          : null

        // Garantir valores booleanos
        this.user.is_super_admin = !!this.user.is_super_admin
        this.user.use_custom_permissions = !!this.user.use_custom_permissions

        // Se não é super admin e não tem permissões customizadas, ativa por padrão
        if (!this.user.is_super_admin && this.user.role_id && !this.user.use_custom_permissions) {
          this.user.use_custom_permissions = true
          // Carregar permissões do role
          const selectedRole = this.roles.find(r => r.id === this.user.role_id)
          if (selectedRole && selectedRole.permissions && Array.isArray(selectedRole.permissions)) {
            this.user.custom_permissions = selectedRole.permissions.map(p => p.id)
          }
        }

        this.loading = false
      }
    },
    async loadRolesAndPermissions () {
      try {
        await this.getRoles()
        await this.getPermissions()
        // Tags pra o multiselect "Tags Permitidas"; falha silenciosa pra não bloquear o form
        try { await this.getTags() } catch (e) { console.warn('Falha ao carregar tags:', e) }
      } catch (err) {
        console.error('Erro ao carregar roles e permissions:', err)
      }
    },
    async open (id = null) {
      this.modal = true
      this.loading = true

      // Carregar roles e permissions primeiro
      await this.loadRolesAndPermissions()

      if (id) {
        await this.loadItem(id)
        return
      }

      // Novo usuário: descobre se o tablet já existe pra mostrar/ocultar a opção
      this.checkTabletExists()

      // Resetar form para novo usuário - Super Admin como default
      this.user = {
        name: '',
        email: '',
        password: '',
        role_id: '',
        is_super_admin: true,
        is_tablet: false,
        use_custom_permissions: false,
        custom_permissions: [],
        allowed_tag_ids: []
      }

      // Aguardar um tick para garantir que as permissões foram carregadas
      await this.$nextTick()

      // Marcar todas as permissões para visualização do Super Admin
      if (this.permissions && Array.isArray(this.permissions) && this.permissions.length > 0) {
        this.user.custom_permissions = this.permissions.map(p => p.id)
      }

      this.loading = false
    },
    async saveUser () {
      this.submitted = true
      this.$v.user.$touch()
      if (this.$v.user.$invalid) {
        window.scroll(0, 0)
        return
      }

      this.loading = true

      // Preparar dados para envio
      const userData = {
        ...this.user,
        // Converter booleanos
        is_super_admin: this.user.is_super_admin ? 1 : 0,
        is_tablet: this.user.is_tablet ? 1 : 0,
        use_custom_permissions: this.user.use_custom_permissions ? 1 : 0
      }

      // Tablet: conta de credenciamento, sem role/permissões/tags. O servidor
      // fixa nome e e-mail; aqui só garante que nada de permissão vai junto.
      if (this.user.is_tablet) {
        userData.role_id = null
        userData.use_custom_permissions = 0
        userData.custom_permissions = []
        userData.allowed_tag_ids = []
        userData.allowed_stations = null
      }

      // Se for super admin, limpar role_id, permissões e tags permitidas
      if (this.user.is_super_admin) {
        userData.role_id = null
        userData.use_custom_permissions = 0
        userData.custom_permissions = []
        userData.allowed_tag_ids = []
        userData.allowed_stations = null
      }

      await this.postData(userData).then(res => {
        let message = 'Sucesso: Dados armazenados com sucesso.'
        let delay = 2500
        successMessage(message, delay)
        this.close()
        this.$emit('created')
      }).catch((err) => {
        const errorMsg = err.response?.data?.message || err.message
        errorMessage('Alerta: ' + errorMsg)
        this.loading = false
      })
    },
    close () {
      this.submitted = false
      this.modal = false

      // Resetar dados
      const allPermissions = (this.permissions && Array.isArray(this.permissions) && this.permissions.length > 0)
        ? this.permissions.map(p => p.id)
        : []

      this.user = {
        name: '',
        email: '',
        password: '',
        role_id: '',
        is_super_admin: true,
        use_custom_permissions: false,
        custom_permissions: allPermissions,
        allowed_tag_ids: [],
        allowed_stations: null
      }

      this.title = 'Novo Usuário'
      this.$v.$reset()
    }
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    },
    'user.is_super_admin' (newVal) {
      if (newVal) {
        this.user.role_id = ''
        this.user.use_custom_permissions = false
        // Marcar todas as permissões
        if (this.permissions && Array.isArray(this.permissions) && this.permissions.length > 0) {
          this.user.custom_permissions = this.permissions.map(p => p.id)
        }
      }
    },
    permissions: {
      handler (newVal) {
        // Quando as permissões são carregadas e o usuário é super admin, marcar todas
        if (this.user.is_super_admin && newVal && Array.isArray(newVal) && newVal.length > 0) {
          if (this.user.custom_permissions.length === 0) {
            this.user.custom_permissions = newVal.map(p => p.id)
          }
        }
      },
      immediate: true
    }
  }
}
</script>
<style scoped>
.vld-overlay.is-full-page {
    z-index: 1051 !important;
}

/* ========== LAYOUT 40/60 ========== */
.row.no-gutters {
  display: flex;
  min-height: 650px;
  margin: 0;
}

.col-left {
  width: 40%;
  padding: 0 24px 20px 0;
  border-right: 1px solid #e9ecef;
}

.col-right {
  width: 60%;
  padding: 0 0 0 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permissions-scroll-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  padding-bottom: 20px;
}

/* ========== SCROLLBAR PERSONALIZADO ========== */
.permissions-scroll-wrapper::-webkit-scrollbar {
  width: 4px;
}

.permissions-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.permissions-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.permissions-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.permissions-header {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 10;
  padding-bottom: 10px;
}

.module-permissions-content {
  padding: 0;
}

/* ========== SEÇÕES ========== */
.section-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
}

.section-title i {
  font-size: 1rem;
}

/* ========== CHIPS DE ESTAÇÃO ========== */
.station-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1.5px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  margin: 0;
}
.station-chip i { font-size: 1rem; }
.station-chip:hover { border-color: #556ee6; color: #556ee6; }
.station-chip.active {
  background: #556ee6;
  border-color: #556ee6;
  color: #fff;
}
.station-chip-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

/* ========== INPUTS COM ÍCONES ========== */
.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.875rem;
}

.input-with-icon .form-control {
  padding-left: 36px;
  height: 38px;
}

/* ========== OPÇÕES DE ROLES ========== */
.role-option {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
}

.role-option:hover {
  border-color: #556ee6;
  background: #f8f9fe;
}

.role-selected {
  border-color: #556ee6;
  background: linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%);
  box-shadow: 0 0 0 1px #556ee6;
}

.role-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f8f9fa;
}
.role-disabled:hover {
  border-color: #e9ecef;
  background: #f8f9fa;
}

.role-header {
  margin-bottom: 8px;
}

.role-header strong {
  font-size: 0.8125rem;
  color: #495057;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.role-header strong .mdi-shield-star {
  font-size: 1rem;
}

.role-description {
  margin: 0;
  font-size: 0.75rem;
  color: #74788d;
  line-height: 1.4;
}

/* ========== PERMISSÕES - LADO DIREITO ========== */
.permissions-notice {
  padding: 14px;
  margin-bottom: 14px;
  background: #f8f9fe;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #495057;
  font-size: 0.8125rem;
  text-align: center;
}

.permissions-notice i {
  margin-right: 8px;
  color: #556ee6;
}

.permissions-notice a {
  color: #556ee6;
  text-decoration: none;
  font-weight: 500;
}

.permissions-notice a:hover {
  text-decoration: underline;
}

/* ========== LISTA DE MÓDULOS ========== */
.module-permission-item {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.module-permission-item:hover {
  box-shadow: 0 1px 3px rgba(85, 110, 230, 0.08);
  border-color: #d1dae6;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.module-header:hover {
  background: #f8f9fa;
}

.module-info {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
}

.module-icon {
  font-size: 1.25rem;
  color: #556ee6;
  background: rgba(85, 110, 230, 0.08);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.module-details h6 {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #495057;
}

.module-description {
  display: block;
  font-size: 0.6875rem;
  color: #74788d;
  margin-top: 2px;
}

.module-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.permission-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
}

.status-full {
  background: rgba(52, 195, 143, 0.15);
  color: #34c38f;
}

.status-partial {
  background: rgba(242, 184, 73, 0.15);
  color: #f1b44c;
}

.status-none {
  background: rgba(116, 120, 141, 0.15);
  color: #74788d;
}

.transition-icon {
  transition: transform 0.2s ease;
  font-size: 1.2rem;
  color: #74788d;
}

/* ========== LISTA DE PERMISSÕES INDIVIDUAIS ========== */
.module-permissions-body {
  border-top: 1px solid #e9ecef;
  background: #fafbfc;
}

.permission-list {
  padding: 12px 14px;
}

.permission-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-bottom: 6px;
  transition: all 0.2s ease;
}

.permission-item:hover {
  border-color: #d1dae6;
  background: #f8f9fe;
}

.permission-info {
  flex: 1;
}

.permission-info strong {
  display: block;
  font-size: 0.75rem;
  color: #495057;
  font-weight: 500;
  margin-bottom: 3px;
}

.permission-description {
  display: block;
  font-size: 0.6875rem;
  color: #74788d;
  line-height: 1.3;
}

/* ========== CUSTOM SWITCH ========== */
.custom-switch .custom-control-label::before {
  background-color: #e3ebf6;
  border: none;
}

.custom-switch .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #556ee6;
  border: none;
}

.custom-switch .custom-control-input:disabled:checked ~ .custom-control-label::before {
  background-color: #34c38f;
  opacity: 0.8;
}

.custom-switch .custom-control-input:disabled ~ .custom-control-label {
  cursor: not-allowed;
}

/* ========== LABELS E FORMS ========== */
label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
}

.form-control {
  font-size: 0.8125rem;
  border-color: #e9ecef;
}

.form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.1rem rgba(85, 110, 230, 0.15);
}

.form-group {
  margin-bottom: 1rem;
}
</style>
